/**
 * BCR report links component — shows test report links when available.
 * Supports both single URLs and JSON objects with multiple comparison types.
 */

import { Card, Typography, Space } from 'antd';
import { FileTextOutlined, LinkOutlined } from '@ant-design/icons';
import type { BcrReportLinks as BcrReportLinksType } from '@/types';
import { getBcrReportLabel } from '@/config/bcrTaskConfig';
import { COMPARISON_RESULT_KEYS, getComparisonReportLabel } from '@/config/comparisonReportLabels';

const { Text, Link } = Typography;

interface BcrReportLinksProps {
  reportLinks: BcrReportLinksType | null;
}

const BcrReportLinks: React.FC<BcrReportLinksProps> = ({ reportLinks }) => {
  if (!reportLinks) return null;

  // 解析可能是 JSON 字符串或 URL 的值
  const parseReportValue = (value: string | null): { isSingle: boolean; url?: string; links?: Record<string, string> } => {
    if (!value) return { isSingle: false };
    try {
      const parsed = JSON.parse(value);
      // 如果是对象且有对比类型的键，则为多链接
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        const hasComparisonKeys = COMPARISON_RESULT_KEYS.some(k => k in parsed);
        if (hasComparisonKeys) {
          return { isSingle: false, links: parsed };
        }
      }
    } catch {
      // 如果不是有效的 JSON，按单个 URL 处理
      return { isSingle: true, url: value };
    }
    return { isSingle: true, url: value };
  };

  const changeValue = reportLinks.change_identification;
  // soc/mcu 测试其实是同一份报告，合并去重后只展示一个链接
  const testReportUrls = Array.from(
    new Set(
      [reportLinks.soc_testing, reportLinks.mcu_testing].filter(
        (u): u is string => typeof u === 'string' && u.length > 0,
      ),
    ),
  );

  const hasChangeReport = !!changeValue;
  const hasTestReport = testReportUrls.length > 0;
  if (!hasChangeReport && !hasTestReport) return null;

  const changeLabel = getBcrReportLabel('change identification');
  const changeParsed = parseReportValue(changeValue);

  return (
    <Card size="small" title={<><FileTextOutlined /> 测试报告</>} className="mt-4">
      <Space orientation="vertical" className="w-full">
        {/* 变更识别报告（可能是单 URL 或多个对比类型链接） */}
        {hasChangeReport && changeParsed.isSingle && changeParsed.url && (
          <div className="flex items-center gap-2">
            <LinkOutlined />
            <Text>{changeLabel}:</Text>
            <Link href={changeParsed.url} target="_blank" rel="noopener noreferrer">
              查看报告
            </Link>
          </div>
        )}
        {hasChangeReport && changeParsed.links && (
          <div className="flex items-start gap-2">
            <LinkOutlined style={{ marginTop: 4 }} />
            <div className="flex-1">
              <div className="font-semibold mb-2">{changeLabel}:</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(changeParsed.links).map(([type, url]) => (
                  typeof url === 'string' && url.startsWith('http') && (
                    <Link
                      key={type}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-1 border rounded"
                    >
                      {getComparisonReportLabel(type)}
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 测试报告（soc/mcu 合并去重，只显示一个链接） */}
        {hasTestReport && (
          <div className="flex items-center gap-2">
            <LinkOutlined />
            <Text>测试报告:</Text>
            {testReportUrls.map((url) => (
              <Link key={url} href={url} target="_blank" rel="noopener noreferrer">
                查看报告
              </Link>
            ))}
          </div>
        )}
      </Space>
    </Card>
  );
};

export default BcrReportLinks;
